import React, { useMemo, useEffect, useState } from 'react';
import i18next from 'i18next';
import { useSelector } from 'react-redux';
import {
  formatEnquiryStatus,
  formatEnquiryType,
  formatNightDayTable,
} from 'utils/formatFieldUtils';

import TableCustom from 'components/common/TableCustom';
import { getCentreId } from 'redux/auth/selectors';
import { useGetEnquiriesQuery } from 'redux/@rtkQuery/contacts';
import {
  DEFAULT_CURRENT_PAGE,
  SORT_DESCEND_VALUE,
} from 'configs/localData/constant';
import { DefaultFilter } from 'redux/@rtkQuery/contacts/types';
import { useParams } from 'react-router';
import {
  DEFAULT_LIMIT_ITEM,
  PAGE_SIZE_OPTIONS,
} from 'containers/Contacts/constants';
import TagCustom from 'components/common/TagCustom';
import GroupActionEnquiry from './GroupActionEnquiry';
import CustomCentreName from '../CustomCentreName';
import { EnquiryTypeStyles } from '../styles';

const DEFAULT_FILTER: DefaultFilter = {
  size: DEFAULT_LIMIT_ITEM,
  page: DEFAULT_CURRENT_PAGE,
};

const EnquiriesTable = () => {
  const [filterValue, setFilterValue] = useState(DEFAULT_FILTER);

  const centreId = useSelector(getCentreId);

  const { id } = useParams();

  const { data, isLoading, refetch, isFetching } = useGetEnquiriesQuery({
    contactId: id,
    ...filterValue,
  });

  const columns = useMemo(
    () => [
      {
        title: i18next.t('centres.centreName'),
        dataIndex: 'centreName',
        key: 'centreName',
        sorter: true,
        width: '30%',
        render: (_, record) => <CustomCentreName record={record} />,
        fixed: 'left',
      },
      {
        title: i18next.t('enquiries.enquiryType'),
        dataIndex: 'enquiryType',
        key: 'enquiryType',
        render: (_, record) => (
          <EnquiryTypeStyles>
            {formatEnquiryType(record?.enquiryTypeName)}
          </EnquiryTypeStyles>
        ),
        width: 250,
        sorter: true,
      },
      {
        title: i18next.t('common.status'),
        dataIndex: 'centreHasNewMessage',
        key: 'centreHasNewMessage',
        render: (centreHasNewMessage, record) =>
          formatEnquiryStatus(
            centreHasNewMessage,
            record.centreResponded,
            record.centreEnquiryStatus,
          ),
        width: 200,
      },
      {
        title: i18next.t('enquiries.enquiryData'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (data) => formatNightDayTable(data),
        width: 250,
        sorter: true,
      },
      {
        title: i18next.t('input.brand.name'),
        dataIndex: 'brandName',
        key: 'brandName',
        sorter: true,
        width: 250,
        render: (value) => value || '-',
      },
      {
        title: i18next.t('centres.subscription'),
        dataIndex: 'subscriptionRank',
        key: 'subscriptionRank',
        sorter: true,
        width: 200,
        render: (_, record) => (
          <TagCustom
            text={record?.subscription?.name}
            backgroundColor={record?.subscription?.backgroundColor}
            color={record?.subscription?.textColor}
          />
        ),
      },
      {
        fixed: 'right',
        width: 80,
        dataIndex: 'id',
        key: 'id',
        render: (id, record) => (
          <GroupActionEnquiry
            id={id}
            centreEnquiryStatus={record?.centreEnquiryStatus}
            packageName={record?.subscription?.name}
            centreId={record?.centreId}
          />
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    refetch();
  }, [centreId, refetch]);

  const onChange = (pagination, filters, sorter) => {
    const formatSort =
      sorter && sorter.columnKey && sorter.order
        ? `${sorter.order === SORT_DESCEND_VALUE ? '-' : ''}${sorter.columnKey}`
        : null;

    setFilterValue({
      sorts: [formatSort],
      size: pagination?.pageSize,
      page: pagination?.current,
    });
  };

  return (
    <TableCustom
      pagination={{
        pageSize: data?.paging?.size || DEFAULT_LIMIT_ITEM,
        current: data?.paging?.page || DEFAULT_CURRENT_PAGE,
        total: data?.paging?.total || 0,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
      isResetStyle
      xScroll={1600}
      data={data?.results || []}
      columns={columns}
      loading={isLoading || isFetching}
      onChange={onChange}
    />
  );
};

export default EnquiriesTable;
